/**
 * Admin → Workflow Engine → Sandbox run.
 *
 * Route: /admin/workflow-engine/sandbox/:runId
 *
 * Live playground for a single sandbox run. The admin can:
 *   - See the current stage / step highlighted on a mini stage-step map.
 *   - Watch the audit log stream (auto-refreshed every 3s).
 *   - Fire canonical events at the subject_id — engine auto-advances if
 *     a trigger matches.
 *   - Manually advance the run one step at a time (for steps without
 *     triggers).
 *   - Cancel the run when done.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { ToolPageShell } from "@/components/AdminTools";
import AssertionsEditor from "./AssertionsEditor";
import {
  ArrowLeft, ArrowRight, Beaker, CheckCircle2, CircleDot, Clock, RefreshCw, Save, X, Zap,
} from "lucide-react";

function SaveScenarioModal({ open, onClose, run, definition, onSaved }) {
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [assertions, setAssertions] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setName("");
      setDescription("");
      setAssertions([]);
      setSaving(false);
    }
  }, [open]);

  if (!open || !run) return null;

  const submit = async (e) => {
    e?.preventDefault?.();
    if (!name.trim()) {
      toast.error("Name required.");
      return;
    }
    setSaving(true);
    try {
      const r = await api.post("/admin/wfe/scenarios/from-run", {
        run_id: run.id,
        name: name.trim(),
        description: description.trim() || null,
        assertions,
      });
      toast.success(`Saved · ${r.data.events.length} events captured`);
      onSaved?.(r.data);
      onClose?.();
    } catch (err) {
      toast.error(formatApiError(err));
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" onClick={onClose} data-testid="save-scenario-modal">
      <div className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm" />
      <form
        onClick={(e) => e.stopPropagation()}
        onSubmit={submit}
        className="relative w-full max-w-lg rounded-2xl bg-white border border-slate-200 shadow-2xl p-6 max-h-[90vh] overflow-y-auto"
      >
        <div className="flex items-start justify-between gap-3 mb-4">
          <div>
            <div className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.18em] font-bold text-violet-600">
              <Save className="w-3 h-3" /> Save scenario
            </div>
            <h2 className="mt-1 font-display text-lg font-bold text-slate-900">
              Turn this run into a reusable test case
            </h2>
            <p className="mt-1 text-xs text-slate-500">
              We&apos;ll capture every event you fired and let you replay it against any version of{" "}
              <strong>{definition?.name}</strong>.
            </p>
          </div>
          <button type="button" onClick={onClose} className="text-slate-400 hover:text-slate-700 p-1">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="space-y-3">
          <div>
            <label className="text-[10px] uppercase tracking-wider font-bold text-slate-500">Name *</label>
            <input
              data-testid="save-scenario-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              autoFocus
              placeholder="Sleep recovery — happy path"
              className="mt-1 w-full border border-slate-300 rounded-lg px-3 py-2 text-sm"
            />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wider font-bold text-slate-500">Description</label>
            <textarea
              data-testid="save-scenario-description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={2}
              placeholder="Optional — when should this scenario be used?"
              className="mt-1 w-full border border-slate-300 rounded-lg px-3 py-2 text-xs resize-none"
            />
          </div>

          <div className="pt-2 border-t border-slate-100">
            <div className="flex items-center justify-between mb-1.5">
              <label className="text-[10px] uppercase tracking-wider font-bold text-slate-500">
                Assertions <span className="text-slate-400 normal-case tracking-normal font-normal">— pass/fail checks after replay</span>
              </label>
            </div>
            <AssertionsEditor
              assertions={assertions}
              onChange={setAssertions}
              testIdPrefix="save-scenario-assertion"
            />
          </div>
        </div>

        <div className="mt-5 flex items-center justify-end gap-2">
          <button type="button" onClick={onClose} disabled={saving} className="text-sm font-semibold text-slate-700 px-3 py-2 rounded-full hover:bg-slate-100">
            Cancel
          </button>
          <button
            type="submit"
            disabled={saving}
            data-testid="save-scenario-submit"
            className="inline-flex items-center gap-1.5 bg-violet-600 hover:bg-violet-700 text-white rounded-full px-4 py-2 text-sm font-semibold disabled:opacity-40"
          >
            <Save className="w-3.5 h-3.5" /> {saving ? "Saving…" : "Save scenario"}
          </button>
        </div>
      </form>
    </div>
  );
}

const RUN_STATUS_COLOR = {
  pending:   "bg-slate-100 text-slate-700",
  running:   "bg-blue-100 text-blue-800",
  paused:    "bg-yellow-100 text-yellow-800",
  completed: "bg-emerald-100 text-emerald-700",
  cancelled: "bg-slate-100 text-slate-500",
  failed:    "bg-rose-100 text-rose-700",
};

function fmtTime(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit", second: "2-digit" });
  } catch { return "—"; }
}

export default function AdminWorkflowSandbox() {
  const { runId } = useParams();
  const [run, setRun] = useState(null);
  const [definition, setDefinition] = useState(null);
  const [events, setEvents] = useState([]);
  const [eventTypes, setEventTypes] = useState([]);
  const [busy, setBusy] = useState(false);
  const [saveScenarioOpen, setSaveScenarioOpen] = useState(false);

  const refresh = useCallback(async () => {
    try {
      const r = await api.get(`/admin/wfe/runs/${runId}`);
      setRun(r.data);
      const [ev, defn] = await Promise.all([
        api.get(`/admin/wfe/runs/${runId}/events`),
        r.data?.workflow_definition_id
          ? api.get(`/admin/wfe/definitions/${r.data.workflow_definition_id}`)
          : Promise.resolve({ data: null }),
      ]);
      setEvents(ev.data?.events || []);
      setDefinition(defn.data);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  }, [runId]);

  useEffect(() => {
    refresh();
    api.get("/admin/wfe/events/registry").then((r) => {
      setEventTypes(r.data?.events || []);
    }).catch(() => setEventTypes([]));
  }, [refresh]);

  // Auto-refresh only while the run is active.
  useEffect(() => {
    if (!run || !["pending", "running", "paused"].includes(run.status)) return undefined;
    const id = setInterval(refresh, 3000);
    return () => clearInterval(id);
  }, [run, refresh]);

  const stagesFlat = useMemo(() => {
    const out = [];
    (definition?.stages || []).forEach((stage) => {
      (stage.steps || []).forEach((step) => {
        out.push({
          stage_id: stage.id, stage_name: stage.name,
          step_id: step.id, step_name: step.name, step_key: step.key,
          expected_days: step.expected_days,
          triggers: step.triggers || [],
        });
      });
    });
    return out;
  }, [definition]);

  // Compute which steps are completed vs current vs upcoming based on events.
  const stepStatuses = useMemo(() => {
    const exited = new Set();
    events.forEach((e) => {
      const fromStep = (e.details?.from || {}).step_id;
      if (e.event_type === "step_advanced" && fromStep) exited.add(fromStep);
    });
    return stagesFlat.map((row) => {
      if (row.step_id === run?.current_step_id) return { ...row, status: "current" };
      if (exited.has(row.step_id)) return { ...row, status: "completed" };
      return { ...row, status: "upcoming" };
    });
  }, [stagesFlat, events, run]);

  const currentStep = stepStatuses.find((s) => s.status === "current");

  const doAdvance = async () => {
    setBusy(true);
    try {
      await api.post(`/admin/wfe/runs/${runId}/advance`, { note: "manual sandbox advance" });
      toast.success("Advanced one step");
      await refresh();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const doCancel = async () => {
    if (!window.confirm("Cancel this sandbox run?")) return;
    setBusy(true);
    try {
      await api.post(`/admin/wfe/runs/${runId}/cancel`, { note: "cancelled from sandbox UI" });
      toast.success("Run cancelled");
      await refresh();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const fireEvent = async (eventType) => {
    if (!run?.subject_id) return;
    setBusy(true);
    try {
      const r = await api.post("/admin/wfe/events/dispatch", {
        event_type: eventType,
        subject_id: run.subject_id,
        source: "sandbox",
        payload: {},
      });
      const advanced = (r.data?.dispatch?.advanced_run_ids || []).length;
      if (advanced > 0) toast.success(`${eventType} → advanced ${advanced} run(s)`);
      else toast(`${eventType} fired · no matching trigger on current step`, { icon: "ℹ️" });
      await refresh();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const isTerminal = run && !["pending", "running", "paused"].includes(run.status);

  return (
    <ToolPageShell
      eyebrow={`Admin · Workflow Engine · Sandbox`}
      title={run ? `${definition?.name || run.workflow_slug} — sandbox run` : "Loading…"}
      actions={
        <div className="flex items-center gap-2">
          <Link
            to={`/admin/workflow-engine/designer/${run?.workflow_definition_id || ""}`}
            data-testid="sandbox-back-to-designer"
            className="inline-flex items-center gap-1.5 bg-white border border-slate-300 text-slate-700 rounded-full px-3 py-2 text-sm font-semibold hover:bg-slate-50"
          >
            <ArrowLeft className="w-3.5 h-3.5" /> Designer
          </Link>
          <button
            type="button"
            onClick={refresh}
            data-testid="sandbox-refresh"
            className="inline-flex items-center gap-1.5 bg-white border border-slate-300 text-slate-700 rounded-full px-3 py-2 text-sm font-semibold hover:bg-slate-50"
          >
            <RefreshCw className="w-3.5 h-3.5" /> Refresh
          </button>
          {events.some((e) => e.event_type === "event_received") && (
            <button
              type="button"
              onClick={() => setSaveScenarioOpen(true)}
              data-testid="sandbox-save-scenario"
              className="inline-flex items-center gap-1.5 bg-violet-600 hover:bg-violet-700 text-white rounded-full px-3 py-2 text-sm font-semibold"
            >
              <Save className="w-3.5 h-3.5" /> Save as scenario
            </button>
          )}
          {run && !isTerminal && (
            <button
              type="button"
              onClick={doCancel}
              disabled={busy}
              data-testid="sandbox-cancel"
              className="inline-flex items-center gap-1.5 bg-rose-600 hover:bg-rose-700 text-white rounded-full px-3 py-2 text-sm font-semibold disabled:opacity-40"
            >
              <X className="w-3.5 h-3.5" /> Cancel run
            </button>
          )}
        </div>
      }
      intro="Live sandbox play area. Fire canonical events at the fake subject and watch the workflow route. No side effects — this run is flagged sandbox and excluded from production analytics."
    >
      {!run ? (
        <div className="text-sm text-slate-500">Loading run…</div>
      ) : (
        <>
          {/* Header */}
          <div className="rounded-2xl border border-slate-200 bg-white p-4 mb-5 flex items-center gap-3 flex-wrap" data-testid="sandbox-run-header">
            <span
              data-testid="sandbox-run-status"
              className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider ${RUN_STATUS_COLOR[run.status] || "bg-slate-100 text-slate-700"}`}
            >
              {run.status}
            </span>
            <span className="text-xs text-slate-500 font-mono">{run.id}</span>
            <span className="text-xs text-slate-500">subject:</span>
            <span className="text-xs font-mono text-slate-900">{run.subject_type}/{run.subject_id}</span>
            {isTerminal && (
              <span className="ml-auto text-[10px] uppercase tracking-wider font-bold text-slate-500">
                terminal — auto-refresh off
              </span>
            )}
          </div>

          <div className="grid lg:grid-cols-12 gap-5">
            {/* LEFT: stage/step map */}
            <div className="lg:col-span-5 rounded-2xl border border-slate-200 bg-white p-4">
              <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500 mb-3">
                Stages · steps
              </div>
              <ol data-testid="sandbox-step-map" className="space-y-1.5">
                {stepStatuses.map((s) => (
                  <li
                    key={s.step_id}
                    data-testid={`sandbox-step-${s.step_id}`}
                    className={`flex items-start gap-2 rounded-lg border p-2 ${
                      s.status === "current" ? "border-blue-500 bg-blue-50" :
                      s.status === "completed" ? "border-emerald-200 bg-emerald-50/50" :
                      "border-slate-200 bg-white"
                    }`}
                  >
                    <span className="mt-0.5 shrink-0">
                      {s.status === "completed" && <CheckCircle2 className="w-4 h-4 text-emerald-600" />}
                      {s.status === "current"   && <CircleDot className="w-4 h-4 text-blue-600" />}
                      {s.status === "upcoming"  && <Clock className="w-4 h-4 text-slate-300" />}
                    </span>
                    <div className="min-w-0 flex-1">
                      <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500">
                        {s.stage_name}
                      </div>
                      <div className={`text-sm font-semibold ${s.status === "current" ? "text-blue-900" : "text-slate-900"}`}>
                        {s.step_name}
                      </div>
                      {s.triggers.length > 0 && (
                        <div className="mt-1 flex flex-wrap gap-1">
                          {s.triggers.map((t) => (
                            <span
                              key={t.id}
                              className="inline-flex items-center rounded-full bg-slate-100 text-slate-600 px-1.5 py-0.5 text-[9px] font-mono"
                            >
                              ⚡ {t.event_type}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                  </li>
                ))}
              </ol>
            </div>

            {/* MIDDLE: Event simulator + manual advance */}
            <div className="lg:col-span-4 space-y-4">
              <div className="rounded-2xl border border-slate-200 bg-white p-4">
                <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500 mb-2">
                  Fire event
                </div>
                {currentStep && currentStep.triggers.length > 0 ? (
                  <div className="mb-3 rounded-md bg-blue-50 border border-blue-200 p-2">
                    <div className="text-[10px] text-blue-800 font-semibold mb-1">
                      Current step waits for:
                    </div>
                    <div className="flex flex-wrap gap-1.5">
                      {currentStep.triggers.map((t) => (
                        <button
                          key={t.id}
                          type="button"
                          onClick={() => fireEvent(t.event_type)}
                          disabled={busy || isTerminal}
                          data-testid={`sandbox-fire-triggered-${t.event_type}`}
                          className="inline-flex items-center gap-1 bg-blue-600 hover:bg-blue-700 text-white rounded-full px-2.5 py-1 text-[11px] font-bold font-mono disabled:opacity-40"
                        >
                          <Zap className="w-3 h-3" /> {t.event_type}
                        </button>
                      ))}
                    </div>
                  </div>
                ) : currentStep ? (
                  <div className="mb-3 text-[11px] text-amber-700 italic">
                    Current step has no triggers. Use <strong>manual advance</strong> below.
                  </div>
                ) : null}

                <div className="text-[10px] text-slate-500 uppercase tracking-wider font-bold mb-1">
                  Or fire any canonical event
                </div>
                <div className="flex flex-wrap gap-1.5" data-testid="sandbox-event-buttons">
                  {eventTypes.map((e) => (
                    <button
                      key={e.type}
                      type="button"
                      onClick={() => fireEvent(e.type)}
                      disabled={busy || isTerminal}
                      data-testid={`sandbox-fire-${e.type}`}
                      className="inline-flex items-center gap-1 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-full px-2 py-0.5 text-[10px] font-mono disabled:opacity-30"
                    >
                      {e.type}
                    </button>
                  ))}
                </div>
              </div>

              <div className="rounded-2xl border border-slate-200 bg-white p-4">
                <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500 mb-2">
                  Manual advance
                </div>
                <p className="text-xs text-slate-600 mb-2">
                  Skip the current step without firing an event. Useful when a step has no triggers.
                </p>
                <button
                  type="button"
                  onClick={doAdvance}
                  disabled={busy || isTerminal}
                  data-testid="sandbox-advance"
                  className="inline-flex items-center gap-1.5 bg-slate-900 hover:bg-slate-800 text-white rounded-full px-3 py-1.5 text-xs font-semibold disabled:opacity-40"
                >
                  <ArrowRight className="w-3.5 h-3.5" /> Advance one step
                </button>
              </div>
            </div>

            {/* RIGHT: audit log */}
            <div className="lg:col-span-3 rounded-2xl border border-slate-200 bg-white p-4 max-h-[600px] overflow-y-auto">
              <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500 mb-2">
                Audit log ({events.length})
              </div>
              <ol data-testid="sandbox-event-log" className="space-y-1.5">
                {events.length === 0 && (
                  <li className="text-xs text-slate-400 italic">No events yet.</li>
                )}
                {events.slice().reverse().map((e) => (
                  <li key={e.id} className="text-[11px] rounded-md bg-slate-50 border border-slate-200 p-1.5">
                    <div className="flex items-center justify-between gap-2">
                      <span className="font-mono text-slate-700">{e.event_type}</span>
                      <span className="text-slate-400">{fmtTime(e.at)}</span>
                    </div>
                    {e.details && e.event_type === "step_advanced" && (
                      <div className="text-[10px] text-slate-500 mt-0.5">
                        {e.details.from?.step_key || "—"} → <strong>{e.details.to?.step_key || "end"}</strong>
                      </div>
                    )}
                    {e.by && <div className="text-[9px] text-slate-400 mt-0.5">by {e.by}</div>}
                  </li>
                ))}
              </ol>
            </div>
          </div>
        </>
      )}
      <SaveScenarioModal
        open={saveScenarioOpen}
        onClose={() => setSaveScenarioOpen(false)}
        run={run}
        definition={definition}
        onSaved={refresh}
      />
    </ToolPageShell>
  );
}
